import { InputNumberDesignTokens } from '@primeuix/themes/types/inputnumber';
import { createButtonTokens, createInputTokens } from '../tokens/components.js';
import { type SemanticTokens, SEMANTIC } from '../tokens/semantic.js';

/**
 * InputNumber component configuration
 * 
 * Colors are controlled by semantic design tokens.
 */
export function createInputnumber(s: SemanticTokens): InputNumberDesignTokens {
  const buttonTokens = createButtonTokens(s);
  const inputTokens = createInputTokens(s);
  return {
  root: {
    transitionDuration: '0.3s'
  },
  button: {
    background: buttonTokens.ghost.bg,
    hoverBackground: buttonTokens.ghost.bgHover,
    activeBackground: buttonTokens.ghost.bgPressed,
    borderColor: buttonTokens.ghost.border,
    hoverBorderColor: buttonTokens.ghost.border,
    activeBorderColor: buttonTokens.ghost.border,
    color: buttonTokens.ghost.fg,
    hoverColor: buttonTokens.ghost.fg,
    activeColor: buttonTokens.ghost.fg,
    width: 'auto',
    borderRadius: buttonTokens.base.radius,
    verticalPadding: buttonTokens.base.paddingY,

  },
  colorScheme: {
    light: {
      button: {
        background: buttonTokens.ghost.bg,
        hoverBackground: buttonTokens.ghost.bgHover,
        activeBackground: buttonTokens.ghost.bgPressed,
        borderColor: buttonTokens.ghost.border,
        hoverBorderColor: buttonTokens.ghost.border,
        activeBorderColor: buttonTokens.ghost.border,
        color: buttonTokens.ghost.fg,
        hoverColor: buttonTokens.ghost.fg,
        activeColor: buttonTokens.ghost.fg,
      },
    },
  },
  css: () => `
    /* Input styling to match p-inputtext - using :root for specificity */
    :root .p-inputnumber .p-inputnumber-input {
      border-color: ${inputTokens.border};
      color: ${inputTokens.fg};
    }
    
    :root .p-inputnumber .p-inputnumber-input:hover {
      border-color: ${inputTokens.border};
    }
    
    :root .p-inputnumber .p-inputnumber-input:focus {
      border-color: ${inputTokens.borderFocus};
      outline: none;
      box-shadow: none;
    }
    
    :root .p-inputnumber .p-inputnumber-input::placeholder {
      color: ${inputTokens.placeholder};
    }
    
    :root .p-inputnumber .p-inputnumber-input.p-invalid {
      border-color: ${inputTokens.borderInvalid};
    }
    
    /* Horizontal layout variant */
    .p-inputnumber-horizontal {
      display: flex;
      gap: ${inputTokens.base.horizontalGap};
      align-items: center;
    }
    
    .p-inputnumber-horizontal .p-inputnumber-input {
      text-align: center;
      padding: ${inputTokens.base.buttonPadding};
      border: 1px solid ${inputTokens.border};
      border-radius: ${inputTokens.base.radius};
      flex: none;
      width: 3rem;
      height: 3rem;
      order: 1;
    }
    
    .p-inputnumber-horizontal .p-inputnumber-input:hover {
      border-color: ${inputTokens.border};
    }
    
    .p-inputnumber-horizontal .p-inputnumber-input:focus {
      border-color: ${inputTokens.borderFocus};
      outline: none;
      box-shadow: none;
    }
    
    .p-inputnumber-horizontal .p-inputnumber-input.p-invalid {
      border-color: ${inputTokens.borderInvalid};
    }
    
    .p-inputnumber-horizontal .p-inputnumber-decrement-button {
      order: 0;
      border-inline-end: 1px solid ${buttonTokens.ghost.border};
    }
    
    .p-inputnumber-horizontal .p-inputnumber-increment-button {
      border-inline-start: 1px solid ${buttonTokens.ghost.border};
      order: 2;
    }
    
    .p-inputnumber-horizontal .p-inputnumber-button {
      padding: ${inputTokens.base.buttonPadding};
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: ${buttonTokens.base.radius};
      background: ${buttonTokens.ghost.bg};
      border-color: ${buttonTokens.ghost.border};
    }
    
    .p-inputnumber-horizontal .p-inputnumber-button:hover {
      background: ${buttonTokens.ghost.bgHover};
      border-color: ${buttonTokens.ghost.border};
    }
    
    .p-inputnumber-horizontal .p-inputnumber-button:active,
    .p-inputnumber-horizontal .p-inputnumber-button:focus {
      background: ${buttonTokens.ghost.bgPressed};
      border-color: ${buttonTokens.ghost.border};
      outline: none;
      box-shadow: none;
    }
    
    :root .p-inputnumber-horizontal .p-inputnumber-button .pi {
      font-size: ${inputTokens.base.iconSize};
      color: ${buttonTokens.ghost.fg};
      width: 100%;
      height: 100%;
    }
  `,
  };
}
export const inputnumber = createInputnumber(SEMANTIC);